import { defineFilter } from '../engine/descriptor';
import type { FilterDefinition, FilterHandler } from '../engine/types';
import {
  type Filter,
  FilterId,
  FilterType,
  type MultiFilter,
  type RangeFilter,
} from '../types/filters';

const DEFAULT_MIN_PAY = 0;
export const PAY_FILTER_MAX = 3_000_000;
const LEGACY_PAY_DEFAULT_MAX_USD = 3_644_500;
const DEFAULT_MAX_PAY = PAY_FILTER_MAX;

/** URL/bookmarks may still store the old default upper bound; normalize to current cap. */
export const migrateLegacyPayDefaultMax = (filter: Filter): Filter => {
  if (filter.id === FilterId.MULTI_PAY && filter.type === FilterType.MULTI) {
    const multi = filter as MultiFilter<RangeFilter>;
    return {
      ...multi,
      instances: multi.instances.map(
        (instance) => migrateLegacyPayDefaultMax(instance) as RangeFilter
      ),
    };
  }
  if (filter.id === FilterId.PAY && filter.type === FilterType.RANGE) {
    const range = filter as RangeFilter;
    if (range.max === LEGACY_PAY_DEFAULT_MAX_USD) {
      return { ...range, max: PAY_FILTER_MAX };
    }
  }
  return filter;
};

const clampPay = (
  pay: number | undefined,
  min: number,
  max: number
): number | undefined => {
  if (pay === undefined) {
    return undefined;
  }
  if (pay < min) {
    return min;
  }
  if (pay > max) {
    return max;
  }
  return pay;
};

const getDefaultBound = (value: number | undefined, fallback: number) => {
  return value !== undefined ? value : fallback;
};

const resolveEffectiveBounds = (
  currentFilterState: RangeFilter,
  definition: FilterDefinition<RangeFilter>
) => {
  const definitionMin = getDefaultBound(definition.min, DEFAULT_MIN_PAY);
  const definitionMax = getDefaultBound(definition.max, DEFAULT_MAX_PAY);
  const rawMin = clampPay(currentFilterState.min, definitionMin, definitionMax);
  const rawMax = clampPay(currentFilterState.max, definitionMin, definitionMax);

  if (rawMin !== undefined && rawMax !== undefined && rawMin > rawMax) {
    return {
      effectiveMin: rawMin,
      effectiveMax: rawMin,
    } as const;
  }

  return {
    effectiveMin: rawMin,
    effectiveMax: rawMax,
  } as const;
};

const isDefaultRange = (
  filterState: RangeFilter,
  definition: FilterDefinition<RangeFilter>
) => {
  const defaultMin = getDefaultBound(definition.min, DEFAULT_MIN_PAY);
  const defaultMax = getDefaultBound(definition.max, DEFAULT_MAX_PAY);

  return (
    filterState.min === defaultMin &&
    (filterState.max === defaultMax ||
      filterState.max === LEGACY_PAY_DEFAULT_MAX_USD)
  );
};

const payHandler: FilterHandler<RangeFilter> = {
  isActive(currentFilterState: RangeFilter) {
    const { min, max } = currentFilterState;
    return min !== undefined || max !== undefined;
  },
  isDefault(currentFilterState: RangeFilter, definition: RangeFilter) {
    return isDefaultRange(currentFilterState, definition);
  },
  getEffectiveFilter(
    currentFilterState: RangeFilter,
    definition: FilterDefinition<RangeFilter>
  ) {
    const { effectiveMin, effectiveMax } = resolveEffectiveBounds(
      currentFilterState,
      definition
    );

    const defaultMin = getDefaultBound(definition.min, DEFAULT_MIN_PAY);
    const defaultMax = getDefaultBound(definition.max, DEFAULT_MAX_PAY);

    // Preserve isUserSubmitted if it was explicitly set to true by the user,
    // even if the values happen to match the defaults
    const isUserSubmitted =
      currentFilterState.isUserSubmitted === true
        ? true
        : !isDefaultRange(currentFilterState, definition);

    const finalMin = effectiveMin ?? defaultMin;
    const finalMax = (() => {
      if (effectiveMax !== undefined) {
        return effectiveMax < finalMin ? finalMin : effectiveMax;
      }
      // For "greater than" filters (only min is set), keep max as undefined
      // Don't set it to finalMin, as that would make it look like a "between" filter
      // The data provider will use the backend-supported pay cap when converting to API format.
      if (
        isUserSubmitted &&
        effectiveMin !== undefined &&
        effectiveMax === undefined
      ) {
        return undefined;
      }
      return isUserSubmitted ? finalMin : defaultMax;
    })();

    return {
      ...currentFilterState,
      min: finalMin,
      max: finalMax,
      isUserSubmitted,
      isVisible: isUserSubmitted,
    };
  },
};

export const payDescriptor = defineFilter({
  definition: {
    id: FilterId.PAY,
    name: 'Pay',
    type: FilterType.RANGE,
    min: DEFAULT_MIN_PAY,
    max: DEFAULT_MAX_PAY,
    isActive: false,
    isVisible: true,
  },
  handler: payHandler,
  multi: { id: FilterId.MULTI_PAY },
});
